import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { FlexCol, FlexRow, UnderlinedSectionTitle } from "../../components/commons-ts/common"
import { Button, Col, Input, Modal, Popconfirm, Row, Table } from "antd"
import { Translated } from "../../utils/translated"
import { Label } from "../../components/commons-ts/input"
import {
  DeleteFilled,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import { CENTRAL_CONTAINER_GAP } from "../../utils/utils"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { ICloudCustomer, ICloudEnterprise } from "../../types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserTie } from "@fortawesome/free-solid-svg-icons"
import { callCloudApiV2 } from "../../utils/cloudApiV2"
import TextArea from "antd/es/input/TextArea"
import { v4 as uuidv4 } from "uuid"
import { ColumnsType } from "antd/es/table"
import { UploadUserImage } from "../../components/commons-ts/uploadUserImage"

const CustomersTable: FC<{
  enterpriseCustomers: ICloudCustomer[] | null
  deleteCustomer: (customerUUID: string) => Promise<void>
  editCustomer: (customer: ICloudCustomer) => Promise<void>
}> = ({ enterpriseCustomers, deleteCustomer, editCustomer }) => {
  const columns = useMemo(
    (): ColumnsType<ICloudCustomer> => [
      {
        title: <Translated keyEn="Name" />,
        dataIndex: "Name",
        key: "Name",
        sorter: (a: ICloudCustomer, b: ICloudCustomer) => a.Name.localeCompare(b.Name),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend",
        render: (_: string, record: ICloudCustomer) => <span>{record.Name}</span>,
      },
      {
        title: <Translated keyEn="Contact" />,
        dataIndex: "Contact",
        key: "Contact",
        render: (_: string, record: ICloudCustomer) => <span>{record.Contact}</span>,
      },
      {
        title: <Translated keyEn="E-mail" />,
        dataIndex: "Email",
        key: "Email",
        render: (_: string, record: ICloudCustomer) => <span>{record.Email}</span>,
      },
      {
        title: <Translated keyEn="Phone" />,
        dataIndex: "PhoneNumber",
        key: "PhoneNumber",
        render: (_: string, record: ICloudCustomer) => <span>{record.PhoneNumber}</span>,
      },
      {
        title: <Translated keyEn="Address" />,
        dataIndex: "PrimaryAddress",
        key: "PrimaryAddress",
        render: (_: string, record: ICloudCustomer) => <span>{record.PrimaryAddress}</span>,
      },
      {
        title: "",
        width: 60,
        key: "actions",
        render: (_: any, record: ICloudCustomer) => {
          return (
            <FlexRow style={{ gap: "0.5rem" }}>
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  editCustomer(record)
                }}
              />
              <Popconfirm
                title={<Translated keyEn="Are you sure you want to delete this customer?" />}
                okText={<Translated keyEn="Yes" />}
                cancelText={<Translated keyEn="No" />}
                placement="topRight"
                onConfirm={() => {
                  deleteCustomer(record.UUID)
                }}
              >
                <Button type="primary" size="small" danger icon={<DeleteFilled />} />
              </Popconfirm>
            </FlexRow>
          )
        },
      },
    ],
    [],
  )

  return (
    <Table
      dataSource={enterpriseCustomers || []}
      columns={columns}
      style={{ width: "100%" }}
      pagination={{
        hideOnSinglePage: true,
      }}
    />
  )
}

export const newCustomer = (): ICloudCustomer => {
  return {
    UUID: uuidv4(),
    Name: "",
    Contact: "",
    Email: "",
    PhoneNumber: "",
    PrimaryAddress: "",
    EnterpriseUUID: "", // This will be set in the parent component
  }
}
const EditCustomerForm: FC<{
  customer: ICloudCustomer | null
  editCustomer: (customer: ICloudCustomer) => Promise<void>
}> = ({ customer, editCustomer }) => {
  const [customerUUID, setCustomerUUID] = useState<string>("")
  const [customerName, setCustomerName] = useState<string>("")
  const [customerContact, setCustomerContact] = useState<string>("")
  const [customerEmail, setCustomerEmail] = useState<string>("")
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<string>("")
  const [customerAddress, setCustomerAddress] = useState<string>("")

  useEffect(() => {
    if (customer === null) {
      setCustomerUUID(uuidv4())
      setCustomerName("")
      setCustomerContact("")
      setCustomerAddress("")
      return
    }
    setCustomerUUID(customer.UUID || "")
    setCustomerName(customer.Name || "")
    setCustomerContact(customer.Contact || "")
    setCustomerAddress(customer.PrimaryAddress || "")
  }, [customer])

  return (
    <Row gutter={[10, 10]} justify="end">
      <Col xs={24}>
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="Title" />
          </Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            size="large"
            variant="filled"
          />
        </FlexCol>
      </Col>
      <Col xs={24}>
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="Contact" />
          </Label>
          <Input
            value={customerContact}
            onChange={(e) => setCustomerContact(e.target.value)}
            size="large"
            variant="filled"
          />
        </FlexCol>
      </Col>
      <Col xs={12}>
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="E-mail" />
          </Label>
          <Input
            prefix={<MailOutlined />}
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            size="large"
            variant="filled"
          />
        </FlexCol>
      </Col>
      <Col xs={12}>
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="Phone" />
          </Label>
          <Input
            prefix={<PhoneOutlined />}
            type="tel"
            value={customerPhoneNumber}
            onChange={(e) => setCustomerPhoneNumber(e.target.value)}
            size="large"
            variant="filled"
          />
        </FlexCol>
      </Col>
      <Col xs={24}>
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="Address" />
          </Label>
          <TextArea
            value={customerAddress}
            onChange={(e) => setCustomerAddress(e.target.value)}
            size="large"
            variant="filled"
          />
        </FlexCol>
      </Col>
      <Col>
        <Button
          type="primary"
          onClick={async () => {
            editCustomer({
              UUID: customerUUID,
              Name: customerName,
              Contact: customerContact,
              Email: customerEmail,
              PhoneNumber: customerPhoneNumber,
              PrimaryAddress: customerAddress,
              EnterpriseUUID: "", // This will be set in the parent component
            })
          }}
        >
          <SaveOutlined />
          <Translated keyEn="Save" />
        </Button>
      </Col>
    </Row>
  )
}

export const EnterpriseSettings: FC = () => {
  const { currentEnterprise, externallySetCurrentEnterprise } = useSyncronizationContext()
  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[] | null>(null)

  const [enterpriseName, setEnterpriseName] = useState<string | null>()
  const [enterprisePictureUUID, setEnterprisePictureUUID] = useState<string | null>(null)
  const [cofracPictureUUID, setCofracPictureUUID] = useState<string | null>(null)

  const [currentCustomer, setCurrentCustomer] = useState<ICloudCustomer | null>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (currentEnterprise === null) {
      return
    }
    setEnterpriseName(currentEnterprise.Name || "")
    setEnterprisePictureUUID(currentEnterprise.EnterprisePictureUUID || null)
    setCofracPictureUUID(currentEnterprise.CofracPictureUUID || null)
  }, [currentEnterprise])

  const loadEnterpriseCustomersCB = useCallback(async () => {
    if (currentEnterprise === null) {
      console.error(`EnterpriseSettings: loadEnterpriseCustomersCB: currentEnterprise is null`)
      return
    }
    let { entity: enterpriseCustomers } = await callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${currentEnterprise.UUID}`,
    )
    if (enterpriseCustomers === null) {
      console.error(
        `EnterpriseSettings: error loading enterprise customers: enterpriseCustomers is null`,
      )
      return
    }
    console.log(`EnterpriseSettings: successfully loaded enterprise customers`, enterpriseCustomers)
    setEnterpriseCustomers(enterpriseCustomers)
  }, [currentEnterprise])

  const deleteCustomerCB = useCallback(async (customerUUID: string) => {
    let { entity } = await callCloudApiV2(`/customer?customer_uuid=${customerUUID}`, {
      method: "DELETE",
    })
  }, [])

  const upsertCustomerCB = useCallback(
    async (customer: ICloudCustomer) => {
      if (currentEnterprise === null) {
        console.error(`EnterpriseSettings: upsertCustomerCB: currentEnterprise is null`)
        return
      }
      let { entity } = await callCloudApiV2(`/customer`, {
        method: "PATCH",
        body: JSON.stringify({
          ...customer,
          EnterpriseUUID: currentEnterprise.UUID,
        }),
      })
      console.log(`EnterpriseSettings: upsertCustomerCB: entity`, entity)
    },
    [currentEnterprise],
  )

  useEffect(() => {
    loadEnterpriseCustomersCB()
  }, [currentEnterprise])

  const handleEnterpriseUpdateCB = useCallback(async (enterprise: ICloudEnterprise) => {
    console.log(`EnterpriseSettings: handleUserUpdateCB`, enterprise)
    callCloudApiV2<ICloudEnterprise>(`/enterprise?enterprise_uuid=${enterprise.UUID}`, {
      method: "PATCH",
      body: JSON.stringify(enterprise),
    })
      .then(({ resp, entity: updatedUser }) => {
        if (!resp.ok) {
          console.error(
            `EnterpriseSettings: handleEnterpriseUpdateCB: error updating enterprise: resp NOK: ${resp.status}`,
          )
          return
        }
        if (updatedUser === null) {
          console.error(
            `EnterpriseSettings: handleEnterpriseUpdateCB: error updating enterprise: null`,
          )
          return
        }
        console.log(
          `EnterpriseSettings: handleEnterpriseUpdateCB: successfully updated enterprise: ${updatedUser.UUID}`,
        )
      })
      .catch((e: any) => {
        console.error(
          `EnterpriseSettings: handleEnterpriseUpdateCB: error updating enterprise: ${e}`,
        )
      })
  }, [])

  const onEnterprisePictureChangedCB = useCallback(
    (pictureUUID: string | null) => {
      if (currentEnterprise === null) {
        return
      }
      let _newCurrentEnterprise: ICloudEnterprise = {
        ...currentEnterprise,
        EnterprisePictureUUID: pictureUUID,
      } // destroy to ensure identity change
      handleEnterpriseUpdateCB(_newCurrentEnterprise)
      externallySetCurrentEnterprise(_newCurrentEnterprise)
    },
    [currentEnterprise],
  )

  const onCofracPictureChangedCB = useCallback(
    (pictureUUID: string | null) => {
      if (currentEnterprise === null) {
        return
      }
      let _newCurrentEnterprise: ICloudEnterprise = {
        ...currentEnterprise,
        CofracPictureUUID: pictureUUID,
      } // destroy to ensure identity change
      handleEnterpriseUpdateCB(_newCurrentEnterprise)
      externallySetCurrentEnterprise(_newCurrentEnterprise)
    },
    [currentEnterprise],
  )

  const onCustomerEdit = useCallback(async (customer: ICloudCustomer) => {
    setIsModalOpen(false)
    await upsertCustomerCB(customer)
    await loadEnterpriseCustomersCB()
  }, [])

  const onCustomerDelete = useCallback(async (customerUUID: string) => {
    await deleteCustomerCB(customerUUID)
    await loadEnterpriseCustomersCB()
  }, [])

  return (
    <FlexCol
      style={{
        width: "100%",
        gap: CENTRAL_CONTAINER_GAP,
      }}
    >
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        title={
          <Label>
            <Translated keyEn="Edit Customer" />
          </Label>
        }
        footer={null}
        width={600}
      >
        <EditCustomerForm customer={currentCustomer} editCustomer={onCustomerEdit} />
      </Modal>
      {/* Header */}
      <FlexRow
        style={{
          justifyContent: "space-between",
        }}
      >
        <UnderlinedSectionTitle>
          <Translated keyEn="Enterprise account" />
        </UnderlinedSectionTitle>
      </FlexRow>
      <Row gutter={[10, 10]} align="top" justify="end">
        <Col flex={1}>
          {
            <FlexCol style={{ gap: 0 }}>
              <Label>
                <Translated keyEn="Enterprise name" />
              </Label>

              <Input value={enterpriseName ?? ""} disabled size="large" variant="filled" />
            </FlexCol>
          }
        </Col>
        {/* LOGO */}
        <Col>
          {/* User picture */}
          <FlexCol
            style={{
              gap: 0,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Label>
              <Translated keyEn="Logo" />
            </Label>
            <div>
              <UploadUserImage
                pictureUUID={enterprisePictureUUID}
                setPictureUUID={onEnterprisePictureChangedCB}
                listType="picture-card"
              />
            </div>
          </FlexCol>
        </Col>
        {/* COFRAC */}
        <Col>
          {/* User picture */}
          <FlexCol
            style={{
              gap: 0,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Label>
              <Translated keyEn="COFRAC" />
            </Label>
            <div>
              <UploadUserImage
                pictureUUID={cofracPictureUUID}
                setPictureUUID={onCofracPictureChangedCB}
                listType="picture-card"
              />
            </div>
          </FlexCol>
        </Col>
        <Col xs={24}>
          <Label>
            <Translated keyEn="Customers" />
          </Label>
          <CustomersTable
            enterpriseCustomers={enterpriseCustomers}
            deleteCustomer={onCustomerDelete}
            editCustomer={async (customer: ICloudCustomer) => {
              setCurrentCustomer(customer)
              setIsModalOpen(true)
            }}
          />
        </Col>
        <Col>
          <Button
            icon={<FontAwesomeIcon icon={faUserTie} />}
            onClick={() => {
              setCurrentCustomer(newCustomer())
              setIsModalOpen(true)
            }}
          >
            <Translated keyEn="Add Customer" />
          </Button>
        </Col>
      </Row>
    </FlexCol>
  )
}
